import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ScrollView, ActivityIndicator } from "react-native";
import { useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";
import { FullScreenLoading, EmptyState } from "@src/components/screen-state";
import { useSupportUserStatus, useTenantId } from "@src/features/tenants";
import { SupportAccessRestricted } from "@src/components/web/SupportAccessRestricted.web";
import { DetectionsList, toDumpEntry } from "@src/components/web/SceneCompareDumps.web";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { useSnapshotHistory, useSignedUrl, type SnapshotHistoryItem } from "@visioni/a3s-media/app";
import { useCameraConfirmedDumps } from "@visioni/a3s-dumping/app";
import { toISODateString } from "@visioni/a3s-codecs";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";

// Collapsed-mode heights for the two sidebar sections. The collapsed section
// shows a small scrollable peek (roughly a header + a couple of rows) while the
// other section takes the remaining space. Tuned to reveal ~2 detection cards
// / the selected snapshot without crowding the expanded section.
const SNAPSHOTS_COLLAPSED_HEIGHT = 135;
const DETECTIONS_COLLAPSED_HEIGHT = 170;

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDateRange(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const fmt = (d: Date) => d.toLocaleString("en-AU", { month: "short", day: "numeric" });
  return `${fmt(s)} – ${fmt(e)}`;
}

function formatTimeDiff(fromIso: string, toIso: string): string {
  const diffMs = Math.abs(new Date(fromIso).getTime() - new Date(toIso).getTime());
  const hours = Math.round(diffMs / (60 * 60 * 1000));
  if (hours < 24) return `~${String(hours)}h prior`;
  const days = Math.round(hours / 24);
  if (days === 1) return "~1d prior";
  return `~${String(days)}d prior`;
}

function findClosestTo24hBefore(snapshots: SnapshotHistoryItem[], fromIndex: number): number {
  const fromTime = new Date(snapshots[fromIndex]!.timestamp).getTime();
  const target = fromTime - 24 * 60 * 60 * 1000;
  let bestIdx = snapshots.length - 1;
  let bestDiff = Infinity;
  // Sorted newest-first — only consider older snapshots (higher indices)
  for (let i = fromIndex + 1; i < snapshots.length; i++) {
    const diff = Math.abs(new Date(snapshots[i]!.timestamp).getTime() - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function SliderView({
  left,
  right,
  colors,
}: {
  left: SnapshotHistoryItem;
  right: SnapshotHistoryItem;
  colors: { selected: string; comparison: string; overlay: string; overlayText: string };
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(0.5);
  const isDragging = useRef(false);

  const leftSrc = useSignedUrl(left.snapshot);
  const rightSrc = useSignedUrl(right.snapshot);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setPosition(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) setPosition(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
    };
    const onUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  if (!rightSrc) {
    return (
      <div
        style={{
          width: "100%",
          aspectRatio: "4/3",
          borderRadius: 8,
          backgroundColor: colors.overlay,
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: "inline-block",
        maxWidth: "100%",
        maxHeight: "100%",
        userSelect: "none",
        borderRadius: 8,
        overflow: "hidden",
        fontFamily: "Inter, sans-serif",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Base image in normal flow — sizes the container */}
      <img src={rightSrc} style={{ display: "block", maxWidth: "100%", maxHeight: "100%", height: "auto" }} />
      {/* Comparison label */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          right: 12,
          background: colors.overlay,
          padding: "4px 10px",
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          color: colors.overlayText,
          borderLeft: `3px solid ${colors.comparison}`,
        }}
      >
        {formatTimestamp(right.timestamp)} ({formatTimeDiff(left.timestamp, right.timestamp)})
      </div>
      {/* Left image overlay with clip */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          clipPath: `inset(0 ${((1 - position) * 100).toFixed(2)}% 0 0)`,
        }}
      >
        {leftSrc && <img src={leftSrc} style={{ display: "block", width: "100%", height: "100%" }} />}
        <div
          style={{
            position: "absolute",
            bottom: 12,
            left: 12,
            background: colors.overlay,
            padding: "4px 10px",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            color: colors.overlayText,
            borderLeft: `3px solid ${colors.selected}`,
          }}
        >
          {formatTimestamp(left.timestamp)} (selected)
        </div>
      </div>
      {/* Slider handle */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: `${(position * 100).toFixed(2)}%`,
          height: "100%",
          width: 3,
          background: colors.selected,
          cursor: "ew-resize",
          transform: "translateX(-50%)",
          zIndex: 10,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 20,
            height: 36,
            background: colors.selected,
            borderRadius: 4,
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>
    </div>
  );
}

interface SceneCompareContentProps {
  cameraKey: string;
  siteId: string;
}

export function SceneCompareContent({ cameraKey, siteId }: SceneCompareContentProps) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const supportStatus = useSupportUserStatus();
  const isSupportUser = supportStatus === "granted";
  const tenantId = useTenantId();
  // Reactive flag reads so the guard re-evaluates when flags hydrate. Previously
  // these used the non-reactive getBooleanFlag(...) whose result the React
  // Compiler froze on first render — see DumpingSearch.web.tsx for the full
  // rationale (cold deep-link froze isEnabled=false → permanent restriction).
  const isEnabled = useBooleanFlag(FEATURE_FLAGS.SCENE_COMPARE);
  // Dumping search is gated independently of Scene Compare: a user can compare
  // snapshots without the follow-on bisect being available yet.
  const isDumpingSearchEnabled = useBooleanFlag(FEATURE_FLAGS.DUMPING_SEARCH);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [offsetDays, setOffsetDays] = useState(0);
  // Which section owns the large share of the shared column. Default keeps the
  // snapshot list prominent (the primary compare workflow); expanding detections
  // flips it so the operator can scroll/page prior dumps comfortably.
  const [columnMode, setColumnMode] = useState<"snapshots-expanded" | "detections-expanded">("snapshots-expanded");

  const now = useMemo(() => new Date(), []);
  const endTime = useMemo(
    () => toISODateString(new Date(now.getTime() - offsetDays * 24 * 60 * 60 * 1000)),
    [now, offsetDays],
  );
  const startTime = useMemo(
    () => toISODateString(new Date(now.getTime() - (offsetDays + 2) * 24 * 60 * 60 * 1000)),
    [now, offsetDays],
  );

  const parsedSiteId = useMemo(() => SiteId.fromString(siteId), [siteId]);
  const parsedCameraKey = useMemo(() => AssetCameraKey.fromString(cameraKey), [cameraKey]);

  const {
    data: snapshots,
    isLoading,
    isFetching,
  } = useSnapshotHistory({
    siteId: parsedSiteId,
    assetCameraKey: parsedCameraKey,
    startTime,
    endTime,
    enabled: isSupportUser && isEnabled,
  });

  const sortedSnapshots = useMemo(() => {
    if (!snapshots) return [];
    return [...snapshots].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [snapshots]);

  // Confirmed dumps for this exact camera slot, newest-first, cursor-paged.
  // Gated by the same support + flag check as the snapshots read so the query
  // never fires for a user who can't see the page.
  const {
    dumps,
    isLoading: dumpsLoading,
    isError: dumpsError,
    hasNextPage: dumpsHasNextPage,
    isFetchingNextPage: dumpsFetchingNextPage,
    fetchNextPage: fetchMoreDumps,
  } = useCameraConfirmedDumps({
    siteId,
    cameraKey,
    enabled: isSupportUser && isEnabled,
  });

  const dumpEntries = useMemo(() => dumps.map(toDumpEntry), [dumps]);

  const clampedIndex = sortedSnapshots.length > 0 ? Math.min(selectedIndex, sortedSnapshots.length - 1) : 0;

  useEffect(() => {
    if (clampedIndex !== selectedIndex) setSelectedIndex(clampedIndex);
  }, [clampedIndex, selectedIndex]);

  const comparisonIndex = useMemo(() => {
    if (sortedSnapshots.length < 2) return 0;
    return findClosestTo24hBefore(sortedSnapshots, clampedIndex);
  }, [sortedSnapshots, clampedIndex]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape") router.back();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [router]);

  const compareColors = {
    selected: theme.colors.success[500] as string,
    comparison: theme.colors.red[600] as string,
    overlay: (theme.colors.mediaOverlay?.backgroundMedium ?? "rgba(0,0,0,0.5)") as string,
    overlayText: (theme.colors.mediaOverlay?.foreground ?? "#fff") as string,
  };

  // Wait while auth settles on a cold deep-link before deciding access. The flag
  // reads above are now reactive (useBooleanFlag) so the guard re-evaluates when
  // flags hydrate — see DumpingSearch.web.tsx for the full rationale.
  if (supportStatus === "pending") {
    return <FullScreenLoading message="Checking access..." />;
  }

  if (!isSupportUser || !isEnabled) {
    return <SupportAccessRestricted />;
  }

  const leftSnapshot = sortedSnapshots.length > 0 ? sortedSnapshots[clampedIndex] : undefined;
  const rightSnapshot = sortedSnapshots.length >= 2 ? sortedSnapshots[comparisonIndex] : undefined;

  // Hand the two compared snapshots to dumping search as the bisect bounds:
  // the older comparison snapshot is the "no waste" bound, the newer selected
  // snapshot is the "waste present" bound.
  // NOTE: Scene Compare is the entry point into a dumping search and should
  // eventually move into features/a3s-dumping alongside the search UI.
  // Require two *distinct* snapshots: when the selected snapshot is the oldest
  // in the window, findClosestTo24hBefore returns the same index, which would
  // hand dumping search a zero-width range.
  const canStartDumpingSearch =
    isDumpingSearchEnabled && !!leftSnapshot && !!rightSnapshot && leftSnapshot.timestamp !== rightSnapshot.timestamp;
  const handleStartDumpingSearch = () => {
    if (!leftSnapshot || !rightSnapshot) return;
    router.push({
      pathname: "/tenants/[tenantId]/sites/[siteId]/cameras/[cameraKey]/dumping-search",
      params: {
        tenantId,
        siteId,
        cameraKey,
        startTime: rightSnapshot.timestamp,
        endTime: leftSnapshot.timestamp,
      },
    });
  };

  return (
    <View flex={1} style={{ overflow: "hidden", fontFamily: "Inter, sans-serif" } as any}>
      <View flex={1} flexDirection="row" style={{ overflow: "hidden", minHeight: 0 }}>
        {/* Slider viewport */}
        <View flex={1} style={{ display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
          <View
            flex={1}
            p="3xl"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {isLoading ?
              <FullScreenLoading message="Loading snapshots..." />
            : !leftSnapshot || !rightSnapshot ?
              <EmptyState
                icon="videoOutline"
                title="Not enough snapshots"
                message="There aren't enough snapshots in this window to compare."
              />
            : <SliderView left={leftSnapshot} right={rightSnapshot} colors={compareColors} />}
          </View>

          {/* Hand off to dumping search to pinpoint the clip that captured the change */}
          {canStartDumpingSearch && (
            <View px="3xl" pb="3xl" alignItems="center">
              <Button label="Find dumping clip" onPress={handleStartDumpingSearch} />
            </View>
          )}
        </View>

        {/* Right column: two collapsible sections sharing the vertical space.
            Only one is "expanded" (owns flex:1) at a time; the other collapses
            to a small scrollable footprint. This keeps the compare slider as
            large as possible while still surfacing prior confirmed dumps. */}
        <View
          width={260}
          borderLeftWidth={1}
          borderColor="borderCard"
          style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          {/* ── Snapshots section ─────────────────────────────────────────── */}
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              // Expanded: take the remaining space. Collapsed: a minimal fixed
              // footprint (still scrollable) so detections can dominate.
              ...(columnMode === "snapshots-expanded" ? { flex: 1 } : { height: SNAPSHOTS_COLLAPSED_HEIGHT }),
            }}
          >
            {/* Header: count in parens + collapse/expand toggle + date range */}
            <View flexDirection="row" alignItems="center" justifyContent="space-between" px="3xl" pt="3xl" pb="md">
              <div
                onClick={() =>
                  setColumnMode(columnMode === "snapshots-expanded" ? "detections-expanded" : "snapshots-expanded")
                }
                style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
              >
                <Icon
                  name={columnMode === "snapshots-expanded" ? "chevronUp" : "chevronDown"}
                  size={16}
                  color={theme.colors.textMuted}
                />
                <Text textStyle="sm/semibold" color="text">
                  {`Snapshots (${String(sortedSnapshots.length)})`}
                </Text>
              </div>
              <Text textStyle="xs/regular" color="textMuted">
                {formatDateRange(startTime, endTime)}
              </Text>
            </View>

            {/* Snapshot list. When collapsed we still render the full list but
                the small height keeps only ~1 row visible; we scroll the
                selected snapshot into view so the collapsed peek shows it. */}
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 12, paddingBottom: 12 }}>
              {/* Loading takes precedence over the empty state: while the
                  snapshot history is (re)fetching with nothing yet to show, a
                  spinner mirrors the main slider's loading state instead of a
                  misleading "No snapshots". `isLoading` covers the first load;
                  the `isFetching && empty` guard covers paging Older/Newer into
                  a window whose snapshots haven't arrived yet. */}
              {isLoading || (isFetching && sortedSnapshots.length === 0) ?
                <View py="5xl" alignItems="center">
                  <ActivityIndicator size="small" color={theme.colors.textMuted} />
                </View>
              : sortedSnapshots.length === 0 ?
                <View py="xl">
                  <EmptyState
                    icon="videoOutline"
                    title="No snapshots"
                    message="No snapshots in this window. Try older or newer."
                  />
                </View>
              : sortedSnapshots.map((snap, i) => {
                  const isSelected = i === clampedIndex;
                  const isComparison = i === comparisonIndex;
                  // In collapsed mode, keep only the selected row mounted so the
                  // tiny viewport peeks at the selected snapshot (the plan's
                  // "only one, ideally the selected one").
                  if (columnMode === "detections-expanded" && !isSelected) return null;
                  return (
                    <div
                      key={snap.timestamp}
                      onClick={() => setSelectedIndex(i)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 12,
                        fontFamily: "monospace",
                        marginBottom: 2,
                        background:
                          isSelected && isComparison ?
                            `linear-gradient(90deg, ${compareColors.selected}22, ${compareColors.comparison}22)`
                          : isSelected ? `${compareColors.selected}22`
                          : isComparison ? `${compareColors.comparison}22`
                          : "transparent",
                        border:
                          isSelected ? `1px solid ${compareColors.selected}`
                          : isComparison ? `1px solid ${compareColors.comparison}`
                          : "1px solid transparent",
                        color: theme.colors.text as string,
                      }}
                    >
                      {formatTimestamp(snap.timestamp)}
                      {isSelected && " (selected)"}
                      {isComparison &&
                        !isSelected &&
                        leftSnapshot &&
                        ` (${formatTimeDiff(leftSnapshot.timestamp, snap.timestamp)})`}
                    </div>
                  );
                })
              }
            </ScrollView>

            {/* Navigation footer — shortened (py "md" not "3xl") and count
                removed (now in the header) to free vertical room for the
                detections section below. */}
            <View
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              px="3xl"
              py="md"
              borderTopWidth={1}
              borderColor="borderCard"
            >
              <Button
                label="‹ Older"
                variant="link"
                variantColor="grey"
                onPress={() => setOffsetDays((d) => d + 1)}
                disabled={isFetching}
              />
              {isFetching ?
                <Text textStyle="xs/regular" color="textMuted">
                  Loading...
                </Text>
              : null}
              <Button
                label="Newer ›"
                variant="link"
                variantColor="grey"
                onPress={() => setOffsetDays((d) => Math.max(0, d - 1))}
                disabled={offsetDays === 0 || isFetching}
              />
            </View>
          </View>

          {/* ── Detections section ────────────────────────────────────────── */}
          <View
            borderTopWidth={1}
            borderColor="borderCard"
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              // Mirror of the snapshots section: expanded owns flex:1, collapsed
              // shows the latest couple of dumps in a small scrollable area.
              ...(columnMode === "detections-expanded" ? { flex: 1 } : { height: DETECTIONS_COLLAPSED_HEIGHT }),
            }}
          >
            <View flexDirection="row" alignItems="center" justifyContent="space-between" px="3xl" pt="3xl" pb="md">
              <div
                onClick={() =>
                  setColumnMode(columnMode === "detections-expanded" ? "snapshots-expanded" : "detections-expanded")
                }
                style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
              >
                <Icon
                  name={columnMode === "detections-expanded" ? "chevronUp" : "chevronDown"}
                  size={16}
                  color={theme.colors.textMuted}
                />
                <Text textStyle="sm/semibold" color="text">
                  {`Detections (${String(dumpEntries.length)}${dumpsHasNextPage ? "+" : ""})`}
                </Text>
              </div>
            </View>

            <DetectionsList
              entries={dumpEntries}
              isLoading={dumpsLoading}
              isError={dumpsError}
              hasNextPage={dumpsHasNextPage}
              isFetchingNextPage={dumpsFetchingNextPage}
              onLoadMore={fetchMoreDumps}
            />
          </View>
        </View>
      </View>
    </View>
  );
}
